/**
 * O CONTRATO DE CARGA DAS ABAS: como a moldura pede a leitura de uma aba e como sabe que ela voltou.
 *
 * O QUE O LEGADO FAZ, e é o alvo (`admin.html:5084-5091`):
 *
 * ```js
 * botao.addEventListener('click', async () => {
 *   await CARREGADOR_ABA_CLIENTE[botao.dataset.aba]?.();   // lê PRIMEIRO
 *   ...classList.add('aba-ativa'); ...aba-conteudo-ativa;  // e só então acende e mostra
 * });
 * ```
 *
 * Três consequências, e as três são comportamento que a pessoa vê:
 *   1. a aba anterior fica na tela até a leitura da nova voltar, e ninguém vê "Carregando...",
 *      campo vazio nem lista vazia ao trocar de aba (transversal T1 da verificação da A3);
 *   2. tocar de novo no botão JÁ ACESO relê os dados (transversal T2);
 *   3. o DOM das abas é um só do começo ao fim da página, então rascunho, anexo, arquivo escolhido
 *      e linha de status sobrevivem à troca de aba (transversal T3). O que some é o que o próprio
 *      carregador reescreve: o Questionário volta com o que está no banco, e o Checklist limpa o
 *      status (`5340`), porque o carregador dele faz isso.
 *
 * COMO UMA ABA ADERE: chamando `useCarregadorDaAba(carregar)` no componente que lê, em vez do
 * `useEffect(() => { void carregar(); }, [carregar])`. Nada mais muda na aba.
 *
 * ADERIR É OPCIONAL, e isso é proposital: as abas das outras frentes continuam funcionando como
 * antes (montam ao aparecer, desmontam ao sair) até adotarem o contrato, e a moldura descobre quem
 * aderiu pelo registro que o gancho faz na montagem, sem lista de nomes para alguém manter.
 */

// Contexto, efeito, efeito de layout e referência do React.
import { createContext, useContext, useEffect, useLayoutEffect, useRef } from 'react';
// O id de cada aba do catálogo.
import type { IdDaAba } from './catalogo.js';

/** O que a moldura oferece a cada aba montada. */
export interface CargaDaAba {
  // Qual aba é esta.
  readonly id: IdDaAba;
  // Quantos toques o botão desta aba já recebeu nesta ficha. Zero é a aba que abriu com a ficha.
  readonly carga: number;
  // A aba está na tela (a moldura desenha as demais escondidas).
  readonly ativa: boolean;
  // A aba avisa que adere ao contrato; a devolução desfaz o registro ao desmontar.
  readonly registrar: (id: IdDaAba) => () => void;
  // A aba avisa que a leitura pedida por aquele toque terminou.
  readonly carregou: (id: IdDaAba, carga: number) => void;
}

/** O contexto, nulo fora da moldura (uma aba aberta fora dela lê uma vez e pronto). */
export const ContextoDaAba = createContext<CargaDaAba | null>(null);

/**
 * O gancho que a aba usa no lugar do efeito de leitura.
 *
 * Lê na montagem e a cada toque novo no botão da aba, e avisa a moldura quando termina. Devolve se
 * a aba está na tela, para quem precisa parar trabalho enquanto está escondida (a recarga de 5 s
 * das Mensagens é o caso, `admin.html:5096-5103`).
 *
 * A FUNÇÃO DE LEITURA VAI POR REFERÊNCIA, e não na lista de dependências: ela é recriada a cada
 * render da aba (fecha sobre estado), e depender dela faria a aba reler a cada tecla digitada. O
 * que dispara leitura é o TOQUE, como no legado.
 */
export function useCarregadorDaAba(carregar: () => Promise<void>): { readonly ativa: boolean; readonly carga: number } {
  const contexto = useContext(ContextoDaAba);
  // A última função de leitura, guardada fora do render para o efeito usar a versão atual.
  const leitura = useRef(carregar);
  useLayoutEffect(() => {
    leitura.current = carregar;
  });
  // A aba ainda está na tela: sem isso, um aviso de leitura chegaria depois da ficha trocar.
  const montada = useRef(true);
  useEffect(() => {
    montada.current = true;
    return () => {
      montada.current = false;
    };
  }, []);

  const id = contexto?.id;
  const carga = contexto?.carga ?? 0;
  const registrar = contexto?.registrar;
  const carregou = contexto?.carregou;

  // O registro é EFEITO DE LAYOUT porque a moldura precisa saber que a aba aderiu ainda no commit
  // em que ela montou: é nesse instante que a moldura decide entre esperar a leitura (aderiu) e
  // mostrar a aba na hora (não aderiu). Efeito de layout de filho roda antes do efeito do pai.
  useLayoutEffect(() => {
    if (!registrar || !id) return undefined;
    return registrar(id);
  }, [registrar, id]);

  // A leitura: na montagem (carga de então) e a cada toque novo.
  useEffect(() => {
    void (async () => {
      try {
        await leitura.current();
      } catch (erro) {
        // O legado também não mostra a aba quando o carregador rejeita: o `await` do clique
        // interrompe o resto do bloco (`admin.html:5086-5091`). Fica igual, com o erro no console.
        console.error('Erro ao carregar a aba:', erro);
        return;
      }
      if (montada.current && carregou && id) carregou(id, carga);
    })();
  }, [carga, carregou, id]);

  return { ativa: contexto?.ativa ?? true, carga };
}
